import { Image } from '@chakra-ui/image';
import { Flex } from '@chakra-ui/react';
import { i18n } from '@lingui/core';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import ArrowRightUp from '../../../../assets/icons/wmd-banner/arrow-right-up.svg';
import RaysIcon from '../../../../assets/icons/wmd-banner/rays.svg';
import StarsIcon from '../../../../assets/icons/wmd-banner/stars.svg';
import WmdEventEnglishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-event-english.svg';
import WmdEventSpanishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-event-spanish.svg';
import WmdPromoEnglishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-promotion-english.svg';
import WmdPromoSpanishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-promotion-spanish.svg';
import WmdWarmupEnglishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-warmup-english.svg';
import WmdWarmupSpanishIcon from '../../../../assets/icons/wmd-banner/wmd-logo-warmup-spanish.svg';
import { useSelectorProfileLocale } from '../../../app';
import { WmdPromoBannerConfig } from '../../../routes/welcome/components/promo-banner/promo-banner.types';
import * as styled from './wmd-banner.styled';

export type WmdBannerVariants = 'promotion' | 'warmup' | 'event';

type WmdBannerProps = {
    config: WmdPromoBannerConfig
};

export const WmdBanner = ({ config }: WmdBannerProps) => {
    const { locale } = useSelectorProfileLocale();
    const [variant, setVariant] = useState<WmdBannerVariants | null>(null);
    const logoIcon = useMemo(() => {
        switch (variant) {
        case 'promotion': return locale === 'es-MX' ? WmdPromoSpanishIcon : WmdPromoEnglishIcon;
        case 'warmup': return locale === 'es-MX' ? WmdWarmupSpanishIcon : WmdWarmupEnglishIcon;
        case 'event': return locale === 'es-MX' ? WmdEventSpanishIcon : WmdEventEnglishIcon;
        default: return null;
        }
    }, [locale, variant]);
    const determineVariant = useCallback(() => {
        const now = new Date();

        if (now >= new Date(config.promotion.startTime) && now < new Date(config.promotion.endTime)) {
            return 'promotion';
        } else if (now >= new Date(config.warmup.startTime) && now < new Date(config.warmup.endTime)) {
            return 'warmup';
        } else if (now >= new Date(config.event.startTime) && now < new Date(config.event.endTime)) {
            return 'event';
        }

        return null;
    }, [config]);

    useEffect(() => {
        const updateVariant = () => {
            const newVariant = determineVariant();

            if (newVariant !== variant) setVariant(newVariant);
        };

        updateVariant();
        const interval = setInterval(updateVariant, 60000);

        return () => clearInterval(interval);
    }, [determineVariant, variant]);

    if (!variant) return null;

    const {
        textLeft,
        textRight,
        subTextLeft,
        buttonText,
        buttonLink
    } = config[variant];
    const Container = styled.getWmdContainer(variant);
    const lokalisedText = {
        left: i18n._(textLeft),
        subLeft: i18n._(subTextLeft || ''),
        right: i18n._(textRight || ''),
        button: i18n._(buttonText || '')
    };

    return (
        <Container>
            <styled.rays>
                <Image src={RaysIcon} w={'100%'} h={'100%'} objectFit={'cover'} />
            </styled.rays>
            <styled.logo>
                {logoIcon && <Image src={logoIcon} w={'100%'} h={'100%'} objectFit={'none'} overflow={'visible'} />}
            </styled.logo>
            <styled.wrapper>
                <Flex justifyContent={'flex-start'} alignItems={'center'} w={'40%'}>
                    {variant === 'promotion' && (
                        <styled.stars>
                            <Image src={StarsIcon} w={'100%'} h={'100%'} objectFit={'none'} />
                        </styled.stars>
                    )}
                    <Flex flexDirection={'column'}>
                        {textLeft && (
                            <styled.textLeft
                                dangerouslySetInnerHTML={{ __html: styled.getLeftTextStyles(lokalisedText.left) }}
                                noOfLines={2}
                            />
                        )}
                        {subTextLeft && (
                            <styled.subTextLeft>{lokalisedText.subLeft}</styled.subTextLeft>
                        )}
                    </Flex>
                </Flex>
                <styled.rightWrapper>
                    {textRight && <styled.textRight noOfLines={3}>{lokalisedText.right}</styled.textRight>}
                    {buttonText && buttonLink && (
                        <styled.button
                            rightIcon={<Image src={ArrowRightUp} />}
                            onClick={() => window.open(buttonLink, '_blank', 'noopener,noreferrer')}
                        >
                            {lokalisedText.button}
                        </styled.button>
                    )}
                </styled.rightWrapper>
            </styled.wrapper>
        </Container>
    );
};
